/*******************************************************
* useLogin Hook
*
* Centralizes all authentication-related API calls for login, account requests,
* and password reset functionality.
*
* Key Features:
* - User login authentication
* - Account request submission
* - Password reset flow (request, validate, change)
* - Craft data fetching for account requests
*
* Usage Example:
* ```tsx
* const {
*   login,
*   requestAccount,
*   readAccountRequestOptions,
*   requestPasswordReset,
*   validatePasswordResetPin,
*   changePassword
* } = useLogin();
* ```
********************************************************/
//########## IMPORTS ##########
// #region IMPORTS
import { useCallback } from 'react';
import {
    normalizeAccountRequestOptions,
    type AccountRequestOptions,
    type AccountRequestOptionsResponse,
    type AccountRequestPayload,
} from '../components/accountRequestOptions';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
// Login types
interface LoginCredentials {
    username: string;
    password: string;
}

interface LoginResponse {
    success: boolean;
    error?: string;
}

// Account Request types
interface AccountRequestData {
    EmployeeId: AccountRequestPayload['EmployeeId'];
    UserName?: AccountRequestPayload['UserName'];
    FirstName: AccountRequestPayload['FirstName'];
    MiddleName?: AccountRequestPayload['MiddleName'];
    LastName: AccountRequestPayload['LastName'];
    NickName?: AccountRequestPayload['NickName'];
    Email: AccountRequestPayload['Email'];
    Supervisor?: AccountRequestPayload['Supervisor'];
    DefaultJobID: AccountRequestPayload['DefaultJobID'];
    Craft: AccountRequestPayload['Craft'];
    Shift: AccountRequestPayload['Shift'];
    Role: AccountRequestPayload['Role'];
}


interface ApiResponse<T> {
    data?: T;
    message: string;
    status: number;
}

// Password Reset types
interface PasswordResetRequestData {
    username: string;
}

interface PasswordResetRequestResponse {
    GUID: string;
    TimeExpires: string; // ISO 8601 timestamp from backend
}

interface PasswordResetValidateData {
    GUID: string;
    PIN: number;
}

interface PasswordResetValidateResponse {
    ValidPIN: boolean;
}

interface PasswordResetChangeData {
    GUID: string;
    PIN: number;
    PASSWORD: string;
}

// Hook props and return types
interface useLoginProps {
    // Optional configuration parameters
    baseUrl?: string;
}

interface AccountRequestResponse {
    success: boolean;
    message: string;
    status: number;
}


//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const DEFAULT_BASE_URL = '';
//#endregion


/**
 * useLogin - Centralized hook for authentication-related API calls
 * 
 * @param {useLoginProps} props - Configuration parameters for the hook
 */
export default function useLogin({ baseUrl = DEFAULT_BASE_URL }: useLoginProps = {}) {
    //########## STATE ##########
    // #region STATE
    //#endregion


    //########## CALLBACKS ##########
    // #region CALLBACKS
    /**
     * Handles user login authentication
     */
    const login = useCallback(async (credentials: LoginCredentials): Promise<LoginResponse> => {
        try {
            const response = await fetch(`${baseUrl}/api/v1/Auth/Login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });

            if (response.ok) {
                return { success: true };
            } else {
                const errorData = await response.text();
                const cleanErrorMessage = errorData.replace(/^"|"$/g, '');
                return {
                    success: false,
                    error: cleanErrorMessage || 'An error occurred while logging in'
                };
            }
        } catch (error) {
            console.error('Error logging in:', error);
            return {
                success: false,
                error: `An error occurred while logging in`
            };
        }
    }, [baseUrl]);

    /**
     * Fetches request form reference data
     */
    const readAccountRequestOptions = useCallback(async (): Promise<ApiResponse<AccountRequestOptions>> => {
        try {
            const response = await fetch(`${baseUrl}/api/v1/Auth/AccountReq`);
            if (response.ok) {
                const result = await response.json() as AccountRequestOptionsResponse;
                return {
                    ...result,
                    data: normalizeAccountRequestOptions(result),
                    message: result.message ?? '',
                    status: response.status,
                };
            }
            return {
                data: { crafts: [], shifts: [] },
                message: 'Failed to fetch account request options',
                status: response.status
            };
        } catch (err) {
            console.error('Error fetching account request options:', err);
            return {
                data: { crafts: [], shifts: [] },
                message: 'Failed to load account request options. Please try again later.',
                status: 500
            };
        }
    }, [baseUrl]);

    /**
     * Submits an account request
     */
    const requestAccount = useCallback(async (data: AccountRequestData): Promise<ApiResponse<AccountRequestResponse>> => {
        try {
            const formattedData = {
                ...data,
                DefaultJobID: typeof data.DefaultJobID === 'string'
                    ? Number(data.DefaultJobID)
                    : data.DefaultJobID
            };

            const response = await fetch(`${baseUrl}/api/v1/Auth/AccountReq`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formattedData),
            });

            if (response.ok) {
                const result = await response.json() as ApiResponse<AccountRequestResponse>;
                return {
                    ...result,
                    status: response.status
                };
            }

            if (response.status === 409) {
                const errorData = await response.json() as ApiResponse<unknown>;
                return {
                    data: {} as AccountRequestResponse,
                    message: errorData.message,
                    status: response.status
                };
            }

            const errorText = await response.text();
            return {
                message: errorText || 'An error occurred while submitting the account request',
                status: response.status
            } as ApiResponse<AccountRequestResponse>;
        } catch (error) {
            console.error('Error submitting account request:', error);
            return {
                message: 'An error occurred while submitting the account request',
                status: 500
            } as ApiResponse<AccountRequestResponse>;
        }
    }, [baseUrl]);

    /**
     * Requests a password reset
     */
    const handleSubmitPasswordReset = useCallback(async (data: PasswordResetRequestData): Promise<PasswordResetRequestResponse> => {
        try {
            //trim 
            const response = await fetch(`${baseUrl}/api/v1/Auth/ResetPasswordRequest`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username: data.username }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText);
            }

            return await response.json() as PasswordResetRequestResponse;
        } catch (error) {
            console.error('Error requesting password reset:', error);
            throw error;
        }
    }, [baseUrl]);

    /**
     * Validates a password reset PIN
     */
    const validatePasswordResetPin = useCallback(async (data: PasswordResetValidateData): Promise<PasswordResetValidateResponse> => {
        try {
            const response = await fetch(`${baseUrl}/api/v1/Auth/ResetPasswordValidate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    GUID: data.GUID,
                    PIN: data.PIN,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                if (errorText.includes('{"ValidPIN":false}')) {
                    throw new Error('Invalid PIN');
                }
                throw new Error(errorText);
            }

            return await response.json() as PasswordResetValidateResponse;
        } catch (error) {
            console.error('Error validating PIN:', error);
            throw error;
        }
    }, [baseUrl]);

    /**
     * Changes password after validation
     */
    const changePassword = useCallback(async (data: PasswordResetChangeData): Promise<{ success: boolean; message?: string }> => {
        try {
            const response = await fetch(`${baseUrl}/api/v1/Auth/ResetPasswordChange`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    GUID: data.GUID,
                    PIN: data.PIN,
                    PASSWORD: data.PASSWORD,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                return {
                    success: false,
                    message: errorText
                };
            }

            return {
                success: true,
                message: 'Your password has been successfully reset. You can now log in with your new password.'
            };
        } catch (error) {
            console.error('Error changing password:', error);
            return {
                success: false,
                message: 'An error occurred. Please try again later.'
            };
        }
    }, [baseUrl]);
    //#endregion


    //########## RETURN ##########
    // #region RETURN
    return {
        login,
        requestAccount,
        readAccountRequestOptions,
        handleSubmitPasswordReset,
        validatePasswordResetPin,
        changePassword,
    };
    //#endregion
}
